import { AccountInfo } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import React, { createContext, useState, useContext, useRef, useMemo } from "react";
import { useLocation } from "react-router";
import { Role } from "./enums/Role";
import { loginRequest } from "../authConfig";

// Create a Context
const GlobalContext = createContext<IGlobalContext>({
  loadingUrls: [],
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  addLoadingUrl: (url: string) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  removeLoadedUrl: (url: string) => {},
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  addNavItem: (item: INavItem) => {},
  navItems: [],
  accountInfo: {
    isKindergarten: false,
    isProvider: false,
    isAssessor: false,
    isManager: false,
    isRoot: false,
    IsRootOrManager: false,
    isInternal: false,
    isExternal: false,
    isDepartment: false,
    isAssessorOrManager: false,
    homeAccountId: "",
    environment: "",
    tenantId: "",
    localAccountId: "",
    username: "",
    email: "",
    role: "",
  },
});

// Create a provider component
export const UserProvider = ({ children }: any) => {
  // Get initial value from localStorage, default to null
  const [loadingUrls, setLoadingUrls] = useState<string[]>([]);

  // Save value to localStorage whenever it changes
  const addLoadingUrl = (url: string) => {
    setLoadingUrls((curr) => [...curr, url]);
  };
  const removeLoadedUrl = (url: string) => {
    setLoadingUrls((curr) => {
      const clone = [...curr];
      const idx = clone.findIndex((x) => x == url);
      if (idx != -1) {
        clone.splice(idx, 1);
      }
      return clone;
    });
  };

  const [navItems, setNavItems] = useState<INavItem[]>([]);
  const location = useLocation();
  const currLocation = useRef(location);
  const { accounts, instance, inProgress } = useMsal();

  const [loggedIn, setLoggedIn] = useState(true);

  const getAccessToken = async () => {
    const accessTokenRequest = {
      scopes: loginRequest.scopes,
      account: accounts[0],
    };
    addLoadingUrl("msal");
    return await instance
      .acquireTokenSilent(accessTokenRequest)
      .then(() => {
        setLoggedIn(true);
      })
      .catch((error) => {
        if (inProgress === "none") {
          if (error.errorCode === "no_account_error") {
            setLoggedIn(false);
          } else {
            instance.acquireTokenRedirect({
              scopes: loginRequest.scopes,
            });
          }
        }
      })
      .finally(async () => {
        removeLoadedUrl("msal");
      });
  };

  const accountInfo = useMemo<KisAccountInfo>(() => {
    if (accounts.length < 1) {
      getAccessToken();
    }
    const account = accounts[0] ?? {};
    const role = account?.idTokenClaims?.["extension_Roles"];
    const acc: KisAccountInfo = {
      ...account,
      isKindergarten: role === Role.kindergarten,
      isProvider: role === Role.provider,
      isAssessor: role === Role.assessor,
      isManager: role === Role.manager,
      isRoot: role === Role.root,
      isDepartment: role === Role.department,
      get IsRootOrManager(): boolean {
        return this.isRoot || this.isManager;
      },
      get isInternal(): boolean {
        return this.isRoot || this.isManager || this.isAssessor;
      },
      get isExternal(): boolean {
        return this.isKindergarten || this.isProvider || this.isDepartment;
      },
      get isAssessorOrManager(): boolean {
        return this.isManager || this.isAssessor;
      },
      email: account.idTokenClaims?.["extension_UserEmail"],
      role,
    };

    return acc;
  }, [accounts, inProgress]);
  const addNavItem = (item: INavItem) => {
    if (location != currLocation.current) {
      currLocation.current = location;
      setNavItems([item]);
    } else {
      setNavItems((curr) => [...curr, item]);
    }
  };

  return (
    (accounts[0] != null || !loggedIn) && (
      <GlobalContext.Provider
        value={{
          loadingUrls,
          addLoadingUrl,
          removeLoadedUrl,
          addNavItem,
          accountInfo,
          navItems,
        }}>
        {children}
      </GlobalContext.Provider>
    )
  );
};

// Custom hook for using the context
export const useGlobalContext = (): IGlobalContext => useContext(GlobalContext);

interface IGlobalContext {
  loadingUrls: string[];
  addLoadingUrl: (url: string) => void;
  removeLoadedUrl: (url: string) => void;
  addNavItem: (item: INavItem) => void;
  accountInfo: KisAccountInfo;
  navItems: INavItem[];
}

export interface INavItem {
  id: string;
  label: string;
  items?: INavItem[];
}

export interface KisAccountInfo extends AccountInfo {
  isKindergarten: boolean;
  isProvider: boolean;
  isDepartment: boolean;
  isAssessor: boolean;
  isManager: boolean;
  isRoot: boolean;
  IsRootOrManager: boolean;
  isInternal: boolean;
  isExternal: boolean;
  isAssessorOrManager: boolean;
  email: any;
  role: any;
}
